// ————————————————————————————————————————————————————————————
// Layout.js — CliniFlow v4
// Design system: Clinical Avant-Garde · Sidebar 264px · Glass header
// ————————————————————————————————————————————————————————————

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { setClinicaSeleccionada } from './api';
import { Modal, Button } from './components/ui';
import CommandPalette from './components/CommandPalette';
import { useClinicaActual } from './context/ClinicaActualContext';
import {
  CalendarDays, Plus, Users, LayoutDashboard, TrendingDown,
  CreditCard, BookOpen, Users2, Settings, LogOut, Dumbbell,
  KeyRound, Building2,
  BarChart2, TrendingUp, Calendar, UserCheck, Target, Ticket,
  Search, FileText, ChevronDown,
} from 'lucide-react';

// —— Rutas ————————————————————————————————————————————————————
const TITULOS = {
  '/':                              'Agenda',
  '/nueva-cita':                    'Nueva Cita',
  '/clases':                        'Clases',
  '/pacientes':                     'Pacientes',
  '/dashboard':                     'Resumen',
  '/dashboard/financiero':          'Financiero',
  '/dashboard/actividad':            'Actividad',
  '/dashboard/trabajadores':        'Trabajadores',
  '/dashboard/objetivos':           'Objetivos',
  '/dashboard/bonos-servicios':     'Bonos y Servicios',
  '/equipo':                        'Equipo',
  '/gastos':                        'Gastos',
  '/cobros':                        'Cobros',
  '/facturacion':                   'Facturación',
  '/objetivos':                     'Objetivos',
  '/info-clinica':                  'Info. Clínica',
  '/config':                        'Configuración',
};

const getTitulo  = p => TITULOS[p] || (p.startsWith('/pacientes/') ? 'Ficha de Paciente' : 'CliniFlow');

const getNavActiva = p => {
  if (p === '/')               return '/';
  if (p === '/nueva-cita')     return '/nueva-cita';
  if (p.startsWith('/clases'))    return '/clases';
  if (p.startsWith('/dashboard')) return '/dashboard';
  if (p.startsWith('/pacientes')) return '/pacientes';
  if (p.startsWith('/equipo'))    return '/equipo';
  if (p.startsWith('/gastos'))    return '/gastos';
  if (p.startsWith('/objetivos')) return '/objetivos';
  if (p.startsWith('/cobros'))    return '/cobros';
  if (p.startsWith('/facturacion')) return '/facturacion';
  if (p.startsWith('/info-clinica')) return '/info-clinica';
  if (p.startsWith('/config'))    return '/config';
  return null;
};

// Branding dinámico
const setClinicaBranding = (nombre, faviconUrl) => {
  document.title = nombre ? `${nombre} · CliniFlow` : 'CliniFlow';
  if (faviconUrl) {
    const link = document.querySelector("link[rel='icon'][type='image/svg+xml']")
      || document.querySelector("link[rel='icon']");
    if (link) link.href = faviconUrl;
  }
};

// —— Navegación ———————————————————————————————————————————————
const ANALYTICS_SUBS = [
  { icon: BarChart2,   label: 'Vista General',     ruta: '/dashboard' },
  { icon: TrendingUp,  label: 'Financiera',        ruta: '/dashboard/financiero' },
  { icon: Calendar,    label: 'Actividad',         ruta: '/dashboard/actividad' },
  { icon: Users2,      label: 'Trabajadores',      ruta: '/dashboard/trabajadores' },
  { icon: Ticket,      label: 'Bonos',             ruta: '/dashboard/bonos-servicios' },
  { icon: Target,      label: 'Objetivos',         ruta: '/dashboard/objetivos' },
];

const NAV_PROPIETARIO = [
  { icon: CalendarDays,    label: 'Agenda',         ruta: '/' },
  { icon: Dumbbell,        label: 'Clases',         ruta: '/clases' },
  { icon: Users,           label: 'Pacientes',      ruta: '/pacientes' },
  { icon: CreditCard,      label: 'Cobros',         ruta: '/cobros' },
  { icon: Users2,          label: 'Equipo',         ruta: '/equipo' },
  { icon: TrendingDown,    label: 'Gastos',         ruta: '/gastos' },
  { icon: FileText,        label: 'Facturación',    ruta: '/facturacion' },
  { icon: LayoutDashboard, label: 'Análisis',       ruta: '/dashboard', expandible: true },
];

const NAV_PROFESIONAL = [
  { icon: CalendarDays, label: 'Agenda',     ruta: '/' },
  { icon: Dumbbell,     label: 'Clases',     ruta: '/clases' },
  { icon: Users,        label: 'Pacientes',  ruta: '/pacientes' },
  { icon: CreditCard,   label: 'Cobros',     ruta: '/cobros' },
  { icon: Users2,       label: 'Equipo',     ruta: '/equipo' },
];

const NAV_INF_PROPIETARIO = [
  { icon: Building2, label: 'Info. Clínica',   ruta: '/info-clinica' },
  { icon: Settings,  label: 'Configuración',  ruta: '/config' },
];

const NAV_INF_PROFESIONAL = [];

// —— Modal cambio de contraseña ——
function ModalCambiarPassword({ open, onCerrar }) {
  const [pwd, setPwd]             = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [show, setShow]           = useState(false);
  const [saving, setSaving]       = useState(false);
  const [ok, setOk]               = useState(false);
  const [err, setErr]             = useState('');

  const handleGuardar = async () => {
    if (pwd.length < 8) { setErr('Mínimo 8 caracteres'); return; }
    if (pwd !== pwdConfirm) { setErr('Las contraseñas no coinciden'); return; }
    setSaving(true); setErr('');
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      setOk(true);
    } catch (e) {
      setErr(e.message || 'Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onCerrar();
    setTimeout(() => { setPwd(''); setPwdConfirm(''); setShow(false); setOk(false); setErr(''); }, 250);
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    borderRadius: 10, border: 'none',
    background: 'var(--bg-hover)',
    color: 'var(--text-1)', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit', transition: 'background .18s, box-shadow .18s',
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Cambiar contraseña"
      width={400}
      footer={ok ? (
        <Button variant="primary" onClick={handleClose} style={{ flex: 1 }}>Cerrar</Button>
      ) : (
        <>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button variant="primary" onClick={handleGuardar} loading={saving} disabled={!pwd || !pwdConfirm}>
            Guardar
          </Button>
        </>
      )}
    >
      {err && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-sub)', border: '1px solid var(--danger-rim)', fontSize: 13, color: 'var(--danger)', marginBottom: 16 }}>
          {err}
        </div>
      )}
      {ok && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--success-sub)', border: '1px solid var(--success-rim)', fontSize: 13, color: 'var(--success)' }}>
          Contraseña actualizada correctamente.
        </div>
      )}

      {!ok && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Nueva contraseña
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                autoFocus
                placeholder="Mínimo 8 caracteres"
                style={{ ...inputStyle, paddingRight: 40 }}
                onFocus={e => { e.target.style.background = 'var(--bg-surface)'; e.target.style.boxShadow = '0 0 0 2px var(--brand-s-sub)'; }}
                onBlur={e => { e.target.style.background = 'var(--bg-hover)'; e.target.style.boxShadow = 'none'; }}
              />
              <button
                type="button" onClick={() => setShow(v => !v)} tabIndex={-1}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex' }}
              >
                <span style={{ fontSize: 14 }}>{show ? '🙈' : '👁'}</span>
              </button>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Confirmar contraseña
            </label>
            <input
              type={show ? 'text' : 'password'}
              value={pwdConfirm}
              onChange={e => setPwdConfirm(e.target.value)}
              placeholder="Repite la contraseña"
              style={inputStyle}
              onFocus={e => { e.target.style.background = 'var(--bg-surface)'; e.target.style.boxShadow = '0 0 0 2px var(--brand-s-sub)'; }}
              onBlur={e => { e.target.style.background = 'var(--bg-hover)'; e.target.style.boxShadow = 'none'; }}
            />
            {pwdConfirm && pwdConfirm !== pwd && (
              <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                Las contraseñas no coinciden
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// —— Layout principal —————————————————————————————————————————
export default function Layout({ children, onLogout, usuario, rol, apiOnline = true }) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const navActiva  = getNavActiva(location.pathname);
  const titulo     = getTitulo(location.pathname);
  const isAgenda   = location.pathname === '/';
  const isPacientes = location.pathname === '/pacientes';
  // Pantallas con buscador flotante (esquina inferior derecha) en vez del de la barra.
  const buscadorFlotante = isAgenda
    || location.pathname === '/cobros'
    || location.pathname === '/equipo';

  const [isMobile,     setIsMobile]     = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [analyticsExp, setAnalyticsExp] = useState(location.pathname.startsWith('/dashboard'));
  const [modalPwd,     setModalPwd]     = useState(false);
  const [clinicaNombre, setClinicaNombre] = useState(null);
  const [clinicaLogo,   setClinicaLogo]   = useState(null);
  // tema siempre light
  const [menuUsuario,  setMenuUsuario]  = useState(false);
  const [nombreProfesional, setNombreProfesional] = useState(null);

  const menuRef = useRef(null);

  useEffect(() => {
    const ck = () => setIsMobile(window.innerWidth < 1024);
    ck();
    window.addEventListener('resize', ck);
    return () => window.removeEventListener('resize', ck);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (location.pathname.startsWith('/dashboard')) setAnalyticsExp(true);
  }, [location.pathname]);

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.querySelector('[role="dialog"]')) return;
      switch (e.key) {
        case 'n': if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate('/nueva-cita'); } break;
        case 'p': if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate('/pacientes'); } break;
        case 'a': if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate('/'); } break;
        case 'd': if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate('/dashboard'); } break;
        case 'e': if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate('/equipo'); } break;
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Branding — datos vienen del ClinicaActualContext
  const { clinica, miProfesional } = useClinicaActual();

  useEffect(() => {
    if (clinica) {
      setClinicaBranding(clinica.nombre, clinica.favicon_url || clinica.logo_url);
      setClinicaNombre(clinica.nombre);
      if (clinica.logo_url) setClinicaLogo(clinica.logo_url);
    }
    return () => { setClinicaBranding(null); };
  }, [clinica]);

  useEffect(() => {
    const n = miProfesional?.nombre || "";
    const a = miProfesional?.apellidos || "";
    if (n) setNombreProfesional(`${n} ${a}`.trim());
  }, [miProfesional]);


  // Tema — siempre light
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
  }, []);

  useEffect(() => {
    if (!menuUsuario) return;
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuUsuario(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuUsuario]);

  const iniciales = (() => {
    if (nombreProfesional) return nombreProfesional[0].toUpperCase();
    const email = usuario?.email || '';
    const meta  = usuario?.user_metadata;
    if (meta?.nombre) return meta.nombre[0].toUpperCase();
    return email[0]?.toUpperCase() || 'U';
  })();

  const tieneAccesoCompleto = rol === 'admin' || rol === 'propietario';
  const navItems    = tieneAccesoCompleto ? NAV_PROPIETARIO    : NAV_PROFESIONAL;
  const navInfItems = tieneAccesoCompleto ? NAV_INF_PROPIETARIO : NAV_INF_PROFESIONAL;

  const isAnalyticsRoute = (path) => path.startsWith('/dashboard');

  // —— Sidebar Content ————————————————————————————————————————
  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Logo + brand */}
      <div style={{
        padding: '20px 20px 16px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, flexShrink: 0,
            background: clinicaLogo ? 'transparent' : 'linear-gradient(135deg, var(--brand-p), var(--primary-container, #1a2b3c))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {clinicaLogo
              ? <img src={clinicaLogo} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 10 }} />
              : <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', fontFamily: 'var(--font-display)', lineHeight: 1 }}>
                  {(clinicaNombre || 'CF')[0]}
                </span>
            }
          </div>
          <div>
            <div style={{
              fontSize: 15, fontWeight: 700, color: 'var(--text-1)',
              letterSpacing: '-0.02em', lineHeight: 1.2,
              fontFamily: 'var(--font-display, "Manrope", system-ui, sans-serif)',
            }}>
              {clinicaNombre || 'CliniFlow'}
            </div>
            <div style={{
              fontSize: 10, fontWeight: 500, color: 'var(--text-3)',
              marginTop: 1,
            }}>
              Panel de Gestión
            </div>
          </div>
        </div>
      </div>

      {/* New appointment CTA */}
      <div style={{ padding: '0 12px 12px' }}>
        <button
          onClick={() => navigate('/nueva-cita')}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            height: 38, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, var(--brand-p), var(--primary-container, #1a2b3c))',
            color: '#ffffff',
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
            transition: 'opacity 0.18s ease, transform 0.1s ease',
            boxShadow: '0 4px 12px rgba(4,22,39,0.15)',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <Plus size={16} strokeWidth={2} /> Nueva Cita
        </button>
      </div>

      {/* Nav */}
      <nav style={{
        flex: 1, padding: '4px 12px', display: 'flex', flexDirection: 'column', gap: 2,
        overflowY: 'auto', overflowX: 'hidden',
      }}>
        {navItems.map(item => {
          const active = item.expandible
            ? isAnalyticsRoute(location.pathname)
            : navActiva === item.ruta;
          return (
            <React.Fragment key={item.ruta + item.label}>
              <div
                onClick={() => {
                  if (item.expandible) {
                    setAnalyticsExp(d => !d);
                    if (!isAnalyticsRoute(location.pathname)) navigate(item.ruta);
                  } else {
                    navigate(item.ruta);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  height: 36, padding: '0 10px',
                  borderRadius: 10, cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  color: active ? 'var(--brand-p-readable)' : 'var(--text-2)',
                  background: active ? 'var(--brand-p-sub)' : 'transparent',
                  fontFamily: 'var(--font-sans)', fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  userSelect: 'none',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >
                <item.icon size={18} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.expandible && (
                  <ChevronDown
                    size={14}
                    strokeWidth={2}
                    style={{
                      color: 'var(--text-3)',
                      transition: 'transform 0.2s ease',
                      transform: analyticsExp ? 'rotate(180deg)' : 'rotate(0deg)',
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>

              {/* Analytics sub-items */}
              {item.expandible && analyticsExp && (
                <div style={{
                  overflow: 'hidden', paddingLeft: 8,
                  marginBottom: 4,
                }}>
                  {ANALYTICS_SUBS.map(sub => {
                    const subActive = location.pathname === sub.ruta;
                    return (
                      <div
                        key={sub.ruta}
                        onClick={() => navigate(sub.ruta)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          height: 30, padding: '0 10px 0 20px',
                          borderRadius: 8, cursor: 'pointer',
                          color: subActive ? 'var(--brand-p-readable)' : 'var(--text-3)',
                          background: subActive ? 'var(--brand-p-sub)' : 'transparent',
                          fontFamily: 'var(--font-sans)', fontSize: 12,
                          fontWeight: subActive ? 600 : 400,
                          transition: 'all 0.12s ease',
                          userSelect: 'none',
                        }}
                        onMouseEnter={e => { if (!subActive) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-1)'; } }}
                        onMouseLeave={e => { if (!subActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-3)'; } }}
                      >
                        <div style={{
                          width: 4, height: 4, borderRadius: '50%',
                          background: subActive ? 'var(--brand-s)' : 'var(--border)',
                          flexShrink: 0,
                        }} />
                        {sub.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Footer section */}
      <div style={{ padding: '8px 12px 12px', flexShrink: 0 }}>
        {/* Bottom nav items */}
        {navInfItems.map(item => {
          const active = navActiva === item.ruta;
          return (
            <div
              key={item.ruta}
              onClick={() => navigate(item.ruta)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                height: 34, padding: '0 10px',
                borderRadius: 10, cursor: 'pointer',
                transition: 'all 0.15s ease',
                color: active ? 'var(--brand-p-readable)' : 'var(--text-2)',
                background: active ? 'var(--brand-p-sub)' : 'transparent',
                fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: active ? 600 : 500,
                userSelect: 'none',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-hover)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; } }}
            >
              <item.icon size={18} strokeWidth={1.5} style={{ flexShrink: 0 }} />
              <span>{item.label}</span>
            </div>
          );
        })}

        {/* Divider */}
        <div style={{ height: 1, background: 'var(--border)', margin: '8px 4px' }} />

        {/* User profile */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setMenuUsuario(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
              transition: 'background 0.12s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 10,
              background: 'var(--brand-s)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
            }}>
              {iniciales}
            </div>
            <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nombreProfesional || usuario?.user_metadata?.nombre || usuario?.email?.split('@')[0] || 'Usuario'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 500, marginTop: 1 }}>
                {tieneAccesoCompleto ? 'Propietario' : 'Profesional'}
              </div>
            </div>
          </div>

          {/* Dropdown menu */}
          {menuUsuario && (
            <div className="cf-dropdown-enter" style={{
              position: 'absolute', bottom: '100%', left: 4, right: 4,
              marginBottom: 4,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12, padding: 4,
              boxShadow: 'var(--shadow-md)',
              zIndex: 100,
            }}>
              <div
                onClick={() => { setModalPwd(true); setMenuUsuario(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 13, color: 'var(--text-2)',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <KeyRound size={14} strokeWidth={1.5} /> Cambiar contraseña
              </div>
              <div
                onClick={onLogout}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 13, color: 'var(--danger)',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut size={14} strokeWidth={1.5} /> Cerrar sesión
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // —— Render ————————————————————————————————————————————————
  return (
    <>
      <div style={{
        fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
        background: 'var(--bg-app)',
        minHeight: '100vh',
        display: 'flex',
      }}>

        {/* Mobile overlay */}
        {isMobile && (
          <div
            className={`cf-sidebar-overlay${mobileOpen ? ' active' : ''}`}
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`cf-sidebar${isMobile && mobileOpen ? ' open' : ''}`}
          style={{
            ...(!isMobile ? {
              width: 264,
              flexShrink: 0,
              background: 'var(--bg-surface)',
              position: 'fixed', top: 0, left: 0, bottom: 0,
              zIndex: 100, overflowY: 'auto',
              overflowX: 'hidden',
            } : {
              background: 'var(--bg-surface)',
              overflowY: 'auto',
              overflowX: 'hidden',
            }),
          }}
        >
          <SidebarContent />
        </aside>

        {/* Main content */}
        <div style={{
          flex: 1,
          marginLeft: isMobile ? 0 : 264,
          display: 'flex', flexDirection: 'column', minHeight: '100vh',
          transition: 'margin-left 0.22s cubic-bezier(.4,0,.2,1)',
          minWidth: 0,
        }}>

          {/* Glass top bar — oculto en la Agenda (escritorio) para ganar espacio vertical */}
          {!(isAgenda && !isMobile) && (
          <header style={{
            height: 56, flexShrink: 0,
            background: 'rgba(247,249,251,0.80)',
            backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 24px',
            position: 'sticky', top: 0, zIndex: 50,
            gap: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isMobile && (
                <button
                  onClick={() => setMobileOpen(true)}
                  aria-label="Abrir menú"
                  style={{ background: 'none', border: 'none', color: 'var(--text-1)', cursor: 'pointer', padding: '4px', display: 'flex', borderRadius: 8 }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                </button>
              )}
              <h1 style={{
                margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-1)',
                letterSpacing: '-0.03em',
                fontFamily: 'var(--font-display, "Manrope", system-ui, sans-serif)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {location.pathname.startsWith('/dashboard/') && (
                  <>
                    <span
                      onClick={() => navigate('/dashboard')}
                      style={{ color: 'var(--text-3)', cursor: 'pointer', fontWeight: 600 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-3)'}
                    >Análisis</span>
                    <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{'\u203A'}</span>
                  </>
                )}
                {location.pathname.startsWith('/pacientes/') && (
                  <>
                    <span
                      onClick={() => navigate('/pacientes')}
                      style={{ color: 'var(--text-3)', cursor: 'pointer', fontWeight: 600 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-3)'}
                    >Pacientes</span>
                    <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{'\u203A'}</span>
                  </>
                )}
                {titulo}
              </h1>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Chip clínica activa — solo admin (multi-tenant switch) */}
              {rol === 'admin' && clinicaNombre && (
                <button
                  onClick={() => { setClinicaSeleccionada(null); navigate('/clinicas'); }}
                  title="Cambiar de clínica"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    height: 36, padding: '0 12px',
                    borderRadius: 10, border: '1px solid var(--border)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-1)', fontSize: 13, fontWeight: 600,
                    fontFamily: 'inherit', cursor: 'pointer',
                    transition: 'background 0.12s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
                >
                  <Building2 size={14} strokeWidth={1.7} />
                  <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {clinicaNombre}
                  </span>
                  <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>· Cambiar</span>
                </button>
              )}

              {/* Search bar */}
              <div
                onClick={() => { document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true })); }}
                style={{
                  display: (isMobile || isPacientes || buscadorFlotante) ? 'none' : 'flex', alignItems: 'center', gap: 8,
                  height: 36, padding: '0 14px', minWidth: 200,
                  borderRadius: 10,
                  background: 'var(--bg-hover)',
                  cursor: 'pointer',
                  transition: 'background 0.12s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              >
                <Search size={14} strokeWidth={1.5} style={{ color: 'var(--text-3)' }} />
                <span style={{ flex: 1, fontSize: 13, color: 'var(--text-3)', fontWeight: 400 }}>Buscar paciente...</span>
              </div>

            </div>
          </header>
          )}

          {/* Page content */}
          <main style={{ flex: 1, overflow: 'auto' }}>
            {children}
          </main>
        </div>
      </div>

      {/* Buscador flotante — Agenda, Cobros y Equipo (escritorio): capa fija
          en la esquina inferior derecha. Sustituye al de la barra superior. */}
      {buscadorFlotante && !isMobile && (
        <div
          onClick={() => { document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true })); }}
          style={{
            position: 'fixed', bottom: 20, right: 24, zIndex: 48,
            display: 'flex', alignItems: 'center', gap: 8,
            height: 40, padding: '0 14px', minWidth: 240,
            borderRadius: 12,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 8px 24px rgba(25,28,30,0.18)',
            cursor: 'pointer',
            transition: 'background 0.12s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
        >
          <Search size={15} strokeWidth={1.5} style={{ color: 'var(--text-3)' }} />
          <span style={{ flex: 1, fontSize: 13, color: 'var(--text-3)', fontWeight: 400 }}>Buscar paciente...</span>
        </div>
      )}

      <CommandPalette />
      <ModalCambiarPassword open={modalPwd} onCerrar={() => setModalPwd(false)} />
    </>
  );
}
